import * as THREE from 'three'
import type RAPIER from '@dimforge/rapier3d-compat'
import worldConfig from '../../assets/data/world_config.json'
import type { PickupType } from './pickups'

export type LootType = PickupType

export type DestructibleType = 'crate' | 'barrel' | string

export interface DestructibleTypeConfig {
  hp: number
  debrisCount: number
  explosionRadius: number
  explosiveDamage?: number
}

export function getDestructibleConfig(type: DestructibleType): DestructibleTypeConfig {
  const destructibles = worldConfig.destructibles as Record<string, DestructibleTypeConfig>
  if (destructibles[type]) {
    return destructibles[type]
  }
  return {
    hp: 40,
    debrisCount: 8,
    explosionRadius: 2.0,
    explosiveDamage: 0,
  }
}

export interface DestructibleConfig {
  id: string
  position: THREE.Vector3
  type?: DestructibleType
  size?: [number, number, number]
  maxHp?: number
  debrisCount?: number
  explosionRadius?: number
  explosiveDamage?: number
  lootDrop?: LootType
}

export class DestructibleObject {
  readonly id: string
  readonly type: DestructibleType
  readonly position: THREE.Vector3
  readonly size: [number, number, number]
  readonly maxHp: number
  readonly debrisCount: number
  readonly explosionRadius: number
  readonly explosiveDamage: number
  readonly lootDrop?: LootType

  hp: number
  alive = true
  mesh: THREE.Mesh | null = null
  collider: RAPIER.Collider | null = null
  body: RAPIER.RigidBody | null = null

  constructor(config: DestructibleConfig) {
    this.id = config.id
    this.type = config.type ?? (config.id.startsWith('barrel') ? 'barrel' : 'crate')
    const typeCfg = getDestructibleConfig(this.type)

    this.position = config.position.clone()
    this.size = config.size ?? (this.type === 'barrel' ? [0.8, 1.2, 0.8] : [1, 1, 1])
    this.maxHp = config.maxHp ?? typeCfg.hp
    this.hp = this.maxHp
    this.debrisCount = config.debrisCount ?? typeCfg.debrisCount
    this.explosionRadius = config.explosionRadius ?? typeCfg.explosionRadius
    this.explosiveDamage = config.explosiveDamage ?? (typeCfg.explosiveDamage ?? 0)
    this.lootDrop = config.lootDrop
  }

  applyDamage(amount: number): {
    destroyed: boolean
    loot?: LootType
    explosiveDamage?: number
    explosionRadius?: number
    debrisCount?: number
  } {
    if (!this.alive) return { destroyed: false }

    this.hp = Math.max(0, this.hp - amount)
    if (this.hp <= 0) {
      this.alive = false
      return {
        destroyed: true,
        loot: this.lootDrop,
        explosiveDamage: this.explosiveDamage,
        explosionRadius: this.explosionRadius,
        debrisCount: this.debrisCount,
      }
    }
    return { destroyed: false }
  }
}

export type LootDropCallback = (loot: LootType, position: THREE.Vector3) => void

export class DestructibleManager {
  readonly objects: DestructibleObject[] = []
  private readonly scene: THREE.Scene
  private lootCallbacks: LootDropCallback[] = []

  constructor(scene: THREE.Scene) {
    this.scene = scene
  }

  get count(): number {
    return this.objects.length
  }

  register(obj: DestructibleObject) {
    if (!this.objects.includes(obj)) {
      this.objects.push(obj)
    }
  }

  onLootDrop(cb: LootDropCallback): () => void {
    this.lootCallbacks.push(cb)
    return () => {
      this.lootCallbacks = this.lootCallbacks.filter((c) => c !== cb)
    }
  }

  private notifyLootDrop(loot: LootType, pos: THREE.Vector3) {
    for (let i = 0; i < this.lootCallbacks.length; i++) {
      this.lootCallbacks[i](loot, pos)
    }
  }

  /**
   * Build the THREE.Mesh, attach it to the scene, and (optionally) spawn a
   * Rapier static rigid body + collider at the object's position. The collider
   * shape is provided as a factory so callers can pick cuboid vs cylinder.
   * Used by createCrate and createBarrel -- the only meaningful difference
   * between those two methods is geometry/material/collider shape, which are
   * now passed in as arguments.
   */
  private spawnMeshAndBody(
    obj: DestructibleObject,
    geom: THREE.BufferGeometry,
    mat: THREE.Material,
    colliderFactory: (RAPIER_REF: typeof RAPIER, size: [number, number, number]) => RAPIER.ColliderDesc,
    world?: RAPIER.World,
    RAPIER_REF?: typeof RAPIER,
  ): void {
    const mesh = new THREE.Mesh(geom, mat)
    mesh.position.copy(obj.position)
    this.scene.add(mesh)
    obj.mesh = mesh

    if (world && RAPIER_REF) {
      const bodyDesc = RAPIER_REF.RigidBodyDesc.fixed().setTranslation(
        obj.position.x,
        obj.position.y,
        obj.position.z,
      )
      const body = world.createRigidBody(bodyDesc)
      const collider = world.createCollider(colliderFactory(RAPIER_REF, obj.size), body)
      obj.body = body
      obj.collider = collider
    }
  }

  createCrate(
    config: DestructibleConfig,
    world?: RAPIER.World,
    RAPIER_REF?: typeof RAPIER,
  ): DestructibleObject {
    const obj = new DestructibleObject({ ...config, type: config.type ?? 'crate' })
    const [w, h, d] = obj.size

    const geom = new THREE.BoxGeometry(w, h, d)
    const mat = new THREE.MeshStandardMaterial({
      color: 0x8b5a2b,
      roughness: 0.6,
      metalness: 0.2,
    })
    this.spawnMeshAndBody(
      obj,
      geom,
      mat,
      (R) => R.ColliderDesc.cuboid(w * 0.5, h * 0.5, d * 0.5),
      world,
      RAPIER_REF,
    )

    this.objects.push(obj)
    return obj
  }

  createBarrel(
    config: DestructibleConfig,
    world?: RAPIER.World,
    RAPIER_REF?: typeof RAPIER,
  ): DestructibleObject {
    const obj = new DestructibleObject({ ...config, type: config.type ?? 'barrel' })
    const [w, h] = obj.size

    const geom = new THREE.CylinderGeometry(w * 0.5, w * 0.5, h, 16)
    const mat = new THREE.MeshStandardMaterial({
      color: 0xcc2211,
      roughness: 0.4,
      metalness: 0.6,
    })
    this.spawnMeshAndBody(
      obj,
      geom,
      mat,
      (R) => R.ColliderDesc.cylinder(h * 0.5, w * 0.5),
      world,
      RAPIER_REF,
    )

    this.objects.push(obj)
    return obj
  }

  damageObject(
    obj: DestructibleObject,
    amount: number,
    world?: RAPIER.World,
  ): {
    destroyed: boolean
    loot?: LootType
    explosiveDamage?: number
    explosionRadius?: number
    debrisCount?: number
  } {
    const res = obj.applyDamage(amount)
    if (res.destroyed) {
      if (obj.mesh) {
        this.scene.remove(obj.mesh)
      }
      if (obj.body && world) {
        world.removeRigidBody(obj.body)
      }
      if (res.loot) {
        this.notifyLootDrop(res.loot, obj.position)
      }
    }
    return res
  }
}
